/**
 * Recovery from a dropped GPU / canvas context (QA-C0-02 Q1). A GPU-process reset or a canvas memory purge blanks
 * every canvas-backed image at once (sprites, HUD portrait and icons, minimap) and used to do it silently.
 *
 * - WebGL `webglcontextlost` / `webglcontextrestored` on the game canvas: logged, a toast says so. Pixi re-sends its
 *   textures itself on restore; the guard then checks a canary, and when the picture is still not back, or the
 *   context stays lost, it saves and reloads (the one rebuild that covers every canvas-backed cache).
 * - 2D canvas loss (`contextlost` on a canary 2D canvas, and a pixel check when the tab comes back): all accelerated
 *   canvases were cleared; the guard saves and reloads at once.
 * - A reload is allowed twice a minute so a broken GPU cannot loop.
 * The WebGPU path already reloads on `device.lost` (gpuBackend.ts). Everything is logged to the console with counts.
 */
export interface ContextGuardOptions {
  /** The game's GL canvas. */
  view: HTMLCanvasElement;
  /** Tells the player (the HUD toast). */
  toast: (text: string) => void;
  /** Writes the save before a reload. */
  save: () => void;
  /** Extra rebuild steps to run after a restore (caches that can rebuild without a reload). */
  rebuild?: () => void;
}

const KEY = 'c0.ctxReloads';
const LIMIT = 2;
const WINDOW_MS = 60_000;

const canReload = (): boolean => {
  const now = Date.now();
  let log: number[];
  try {
    log = (JSON.parse(sessionStorage.getItem(KEY) ?? '[]') as number[]).filter((t) => now - t < WINDOW_MS);
  } catch {
    log = [];
  }
  if (log.length >= LIMIT) return false;
  log.push(now);
  try {
    sessionStorage.setItem(KEY, JSON.stringify(log));
  } catch {
    // not remembered
  }
  return true;
};

export function watchContexts(o: ContextGuardOptions): { canaryOk: () => boolean } {
  const counts = { glLost: 0, glRestored: 0, canvas2dLost: 0 };
  let reloading = false;
  let lostTimer = 0;

  const report = (what: string): void => {
    const canvases = document.querySelectorAll('canvas').length;
    console.warn(`[contextGuard] ${what}; canvases=${canvases} ${JSON.stringify(counts)} t=${Math.round(performance.now())}ms`);
  };
  const reload = (why: string): void => {
    if (reloading) return;
    if (!canReload()) {
      report(`${why}: reload skipped (twice already within a minute)`);
      return;
    }
    reloading = true;
    report(`${why}: saving and reloading`);
    o.toast('ภาพขัดข้อง · กำลังโหลดใหม่');
    try {
      o.save();
    } catch {
      // the pagehide save still runs
    }
    window.setTimeout(() => location.reload(), 900);
  };

  // A small accelerated 2D canvas that is only drawn once: when it reads back empty, canvases were dropped.
  const canary = document.createElement('canvas');
  canary.width = canary.height = 4;
  const cg = canary.getContext('2d');
  const paint = (): void => {
    if (!cg) return;
    cg.fillStyle = '#fe01fe';
    cg.fillRect(0, 0, 4, 4);
  };
  paint();
  const probe = document.createElement('canvas');
  probe.width = probe.height = 1;
  const pg = probe.getContext('2d', { willReadFrequently: true });
  const canaryOk = (): boolean => {
    if (!cg || !pg) return true;
    pg.clearRect(0, 0, 1, 1);
    pg.drawImage(canary, 0, 0);
    return pg.getImageData(0, 0, 1, 1).data[3]! > 0;
  };
  canary.addEventListener('contextlost', () => {
    counts.canvas2dLost++;
    report('2D canvas context lost');
    reload('2D canvases were cleared');
  });
  canary.addEventListener('contextrestored', () => report('2D canvas context restored'));

  o.view.addEventListener('webglcontextlost', () => {
    counts.glLost++;
    report('WebGL context lost');
    o.toast('ภาพขัดข้อง · กำลังกู้ภาพ');
    window.clearTimeout(lostTimer);
    lostTimer = window.setTimeout(() => reload('WebGL context not restored'), 5000);
  });
  o.view.addEventListener('webglcontextrestored', () => {
    counts.glRestored++;
    window.clearTimeout(lostTimer);
    report('WebGL context restored');
    try {
      o.rebuild?.();
    } catch (e) {
      console.warn('[contextGuard] rebuild failed', e);
    }
    // Pixi re-sends its textures on the next frames; if the canvases did not survive, reload.
    window.setTimeout(() => {
      if (canaryOk()) o.toast('กู้ภาพแล้ว');
      else reload('canvases blank after the restore');
    }, 1500);
  });

  // Dropped while the tab was in the background: nothing fires, so look when it returns and every few seconds.
  const check = (): void => {
    if (document.visibilityState !== 'visible' || reloading) return;
    if (!canaryOk()) {
      counts.canvas2dLost++;
      reload('canary canvas blank');
    }
  };
  document.addEventListener('visibilitychange', check);
  window.setInterval(check, 4000);
  (window as unknown as Record<string, unknown>).__ctx = { counts, canaryOk, check };
  return { canaryOk };
}
